import { SettingKey } from 'data/ppoints/constants';
import useCheckoutMapSettings from 'features/settings/components/ppoints/design-settings/CheckoutMapSettings/hooks/useCheckoutMapSettings';
import useCheckoutMapSettingsSWR from 'features/settings/components/ppoints/design-settings/CheckoutMapSettings/hooks/useCheckoutMapSettingsSWR';
import useErrorBanner from 'hooks/error-banner/useErrorBanner';
import { useCarrierService } from 'hooks/ppoints/useCarrierService';
import { useCheckoutMapSettingsMapper } from 'hooks/useCheckoutMapSettingsMapper';
import useSettings from 'hooks/useSettings';
import { IntegrationModeSetting } from 'models/ppoints/settings/SettingsToEcom';
import { toggleDeliveryCustomizationMutation } from 'mutations/shopify-delivery-customization/toggle-delivery-customization';
import { useCallback, useState } from 'react';

const useActivateIntegrationMode = () => {
    const carrierService = useCarrierService();
    const integrationModeSetting = useSettings<IntegrationModeSetting>(SettingKey.integration_mode);
    const { setBanner, emptyBanner } = useErrorBanner();
    const { data: checkoutMapSettings, mutate: mutateCheckoutMapSettings } = useCheckoutMapSettingsSWR();
    const { saveCheckoutMapSettings } = useCheckoutMapSettings();
    const { mapFromVo } = useCheckoutMapSettingsMapper();

    const [showCarrierServiceWarning, setShowCarrierServiceWarning] = useState(false);

    const setActivateErrorBanner = useCallback(() => {
        setBanner({
            titleKey: 'WIZARD.INTEGRATION_MODE_STEP.ACTIVATE_ERROR.TITLE',
            descriptionKey: 'WIZARD.INTEGRATION_MODE_STEP.ACTIVATE_ERROR.DESCRIPTION',
            tone: 'warning',
            withSupportActions: true,
        });
    }, [setBanner]);

    const prepareCarrierService = useCallback(
        async (mode: IntegrationModeSetting): Promise<boolean> => {
            if (mode === IntegrationModeSetting.checkout) {
                const res = await carrierService.createCarrierServiceMutation.mutateAsync();
                if (res.success === false) {
                    setShowCarrierServiceWarning(true);
                    return false;
                }
                setShowCarrierServiceWarning(false);
                return true;
            }

            const res = await carrierService.deleteCarrierServiceMutation.mutateAsync();
            if (res.success === false) {
                setBanner({
                    titleKey: 'SETTINGS.MAP_DESIGN.ERROR_CARRIER_SERVICE.TITLE',
                    descriptionKey: 'SETTINGS.MAP_DESIGN.ERROR_CARRIER_SERVICE.CONTENT',
                    tone: 'warning',
                    withSupportActions: true,
                });
                return false;
            }
            setShowCarrierServiceWarning(false);
            return true;
        },
        [carrierService.createCarrierServiceMutation, carrierService.deleteCarrierServiceMutation, setBanner]
    );

    const saveGoogleMapApiKey = useCallback(
        async (googleMapApiKey: string): Promise<boolean> => {
            try {
                await saveCheckoutMapSettings(
                    mapFromVo({ google_map_api_key: googleMapApiKey.trim() }, checkoutMapSettings || [])
                );
                await mutateCheckoutMapSettings();
                return true;
            } catch {
                setBanner({
                    titleKey: 'SETTINGS.CHECKOUT_MAP_INTEGRATION.SAVE_ERROR.TITLE',
                    descriptionKey: 'SETTINGS.CHECKOUT_MAP_INTEGRATION.SAVE_ERROR.CONTENT',
                    tone: 'warning',
                    withSupportActions: true,
                });
                return false;
            }
        },
        [saveCheckoutMapSettings, mapFromVo, checkoutMapSettings, mutateCheckoutMapSettings, setBanner]
    );

    const activateIntegrationMode = useCallback(
        async (mode: IntegrationModeSetting, googleMapApiKey?: string): Promise<boolean> => {
            emptyBanner();
            shopify.loading(true);

            if (mode === IntegrationModeSetting.thank_you_page_map && googleMapApiKey?.trim()) {
                const keySaved = await saveGoogleMapApiKey(googleMapApiKey);
                if (!keySaved) return false;
            }

            try {
                const carrierServiceReady = await prepareCarrierService(mode);
                if (!carrierServiceReady) return false;
            } catch {
                setActivateErrorBanner();
                return false;
            }

            const saved = await integrationModeSetting.setSettings(mode);

            if (!saved) {
                setActivateErrorBanner();
                return false;
            }

            const deliveryCustomizationActive = mode === IntegrationModeSetting.map;

            toggleDeliveryCustomizationMutation(deliveryCustomizationActive).catch(() => {
                setBanner({
                    titleKey: 'SETTINGS.ERROR_DELIVERY_CUSTOMIZATION.TITLE',
                    descriptionKey: deliveryCustomizationActive
                        ? 'SETTINGS.ERROR_DELIVERY_CUSTOMIZATION.ACTIVATE'
                        : 'SETTINGS.ERROR_DELIVERY_CUSTOMIZATION.DEACTIVATE',
                    tone: 'warning',
                    withSupportActions: true,
                });
            });

            shopify.loading(false);
            return true;
        },
        [
            prepareCarrierService,
            integrationModeSetting,
            saveGoogleMapApiKey,
            setActivateErrorBanner,
            setBanner,
            emptyBanner,
        ]
    );

    return {
        activateIntegrationMode,
        showCarrierServiceWarning,
        isLoading: carrierService.isLoading,
    };
};

export default useActivateIntegrationMode;
